import {
    Box,
    Typography,
    Card,
    CardMedia,
    CardContent,
    Chip,
    useTheme,
    useMediaQuery,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Button,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Radio,
    RadioGroup,
    FormControlLabel,
    Badge,
    Drawer,
} from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ProductModal from './ProductModal';
import SNSFloatingBar from '@/components/SNS/SNSFloatingBar';
import { useLoaderData, useLocation } from 'react-router-dom';
import { Category, Product, getProductList, getProductDetail, Vendor } from '@/services/matching.service';
import useCategoryStore from '@/store/categoryStore';
import { isLogined } from '@/services/mallUser.service';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';

// API 응답을 UI에 맞게 변환하는 함수
const transformApiToUiProduct = (apiProduct: Product) => {
    const representMatchingProduct = apiProduct.matchingProducts.find(
        (product) => product.id === apiProduct.representProductId,
    );

    // 할인율 계산 함수
    const calculateDiscount = () => {
        if (representMatchingProduct?.discountMethod === 'PERCENT') {
            return representMatchingProduct.discountValue || 0;
        } else {
            const marketPrice = representMatchingProduct?.marketPrice || 0;
            const supplyPrice = representMatchingProduct?.supplyPrice || 0;
            if (marketPrice === 0 || supplyPrice >= marketPrice) return 0;
            return Math.floor(((marketPrice - supplyPrice) / marketPrice) * 100);
        }
    };

    const discount = calculateDiscount();
    return {
        id: apiProduct.id,
        image: apiProduct.representImageUrl,
        title: apiProduct.groupTitle,
        price: representMatchingProduct?.supplyPrice || 0,
        originalPrice: representMatchingProduct?.marketPrice || 0,
        discount,
        isSale: discount > 0,
        brand: apiProduct.vendor?.nickname || '',
        seller: apiProduct.matchingSponsorshipCondition?.shippingReturnAddress?.recipientAddressDetail || '',
    };
};

const ProductList = () => {
    const theme = useTheme();
    const location = useLocation();
    const isNewProductPage = location.pathname.endsWith('/new');
    const isBestNewPage = location.pathname.endsWith('/best-new');
    const isTodayNewPage = location.pathname.endsWith('/today-new');
    const isDiscountPage = location.pathname.endsWith('/discount');
    const isSoldOutPage = location.pathname.endsWith('/sold-out');
    const [selectedTab, setSelectedTab] = useState(isNewProductPage ? 1 : 0);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const gridColumns = isMobile ? 2 : isTablet ? 4 : 7;
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const { setIsOpen: setLoginDialogOpen } = useLoginFormDialogStore((state) => state.action);

    // 모달 상태
    const [open, setOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    // 무한 스크롤 상태
    const [products, setProducts] = useState<Product[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastProductRef = useRef<HTMLDivElement | null>(null);

    // 필터 모달 상태
    const [filterOpen, setFilterOpen] = useState(false);
    const [priceRange, setPriceRange] = useState('all');
    const [customMinPrice, setCustomMinPrice] = useState('');
    const [customMaxPrice, setCustomMaxPrice] = useState('');
    const [tempMinPrice, setTempMinPrice] = useState('');
    const [tempMaxPrice, setTempMaxPrice] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

    // 초기 데이터 로드
    const { vendor, productList } = useLoaderData() as {
        vendor: Vendor;
        productList: { data: Product[]; count: number; page: number; pageCount: number; total: number };
    };

    const fetchAllCategories = useCategoryStore((state) => state.fetchAllCategories);

    useEffect(() => {
        fetchAllCategories();
    }, []);

    // 로그인 상태 확인
    useEffect(() => {
        setIsLoggedIn(isLogined());

        // 로그인 상태 변경 감지를 위한 주기적 확인
        const interval = setInterval(() => {
            setIsLoggedIn(isLogined());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // 초기 데이터 설정
    useEffect(() => {
        setProducts(productList.data);
        setPage(productList.page);
        setHasMore(productList.page < productList.pageCount);
    }, [productList]);

    // Intersection Observer 설정
    const lastProductElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setPage((prevPage) => prevPage + 1);
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore],
    );

    // 페이지 변경시 데이터 로드
    useEffect(() => {
        const loadMoreProducts = async () => {
            if (page === 1) return; // 초기 데이터는 이미 로드됨

            try {
                setLoading(true);
                const vendorFilter = `filter=vendor.id||$eq||${vendor.id}`;

                // 각 페이지에 맞는 필터 추가
                let filter = vendorFilter;
                if (isNewProductPage) {
                    const oneMonthAgo = new Date();
                    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                    const formattedDate = oneMonthAgo.toISOString();
                    filter = `${vendorFilter}&filter=createdAt||$gte||${formattedDate}`;
                } else if (isBestNewPage) {
                    filter = `${vendorFilter}&filter=isBestNew||$eq||t`;
                } else if (isTodayNewPage) {
                    filter = `${vendorFilter}&filter=isTodayNew||$eq||t`;
                } else if (isDiscountPage) {
                    filter = `${vendorFilter}&filter=isDiscount||$eq||t`;
                } else if (isSoldOutPage) {
                    filter = `${vendorFilter}&filter=isSoldOut||$eq||t`;
                }

                const newData = await getProductList({
                    page,
                    limit: 20,
                    filter,
                });

                setProducts((prev) => [...prev, ...newData.data]);
                setHasMore(page < newData.pageCount);
            } catch (error) {
                console.error('Failed to load more products:', error);
            } finally {
                setLoading(false);
            }
        };

        loadMoreProducts();
    }, [page, isNewProductPage, isBestNewPage, isTodayNewPage, isDiscountPage, isSoldOutPage, vendor.id]);

    // 상품 클릭 핸들러
    const handleOpen = async (originalProduct: Product) => {
        try {
            // 최신 상품 정보 조회
            const updatedProduct = await getProductDetail({ productId: originalProduct.id });
            setSelectedProduct(updatedProduct);
            setOpen(true);
        } catch (error) {
            console.error('Failed to fetch product details:', error);
            // API 호출 실패시 기존 데이터라도 보여주기
            setSelectedProduct(originalProduct);
            setOpen(true);
        }
    };

    // 모달 닫기 핸들러
    const handleClose = () => {
        setOpen(false);
        setSelectedProduct(null);
    };

    // URL 경로가 변경될 때마다 선택된 탭 업데이트
    useEffect(() => {
        setSelectedTab(isNewProductPage ? 1 : 0);
    }, [isNewProductPage]);

    // 검색어 디바운스 처리
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    // 가격 필터링 함수
    const filterByPrice = (product: Product) => {
        const price = transformApiToUiProduct(product).price;

        switch (priceRange) {
            case 'under10k':
                return price <= 10000;
            case '10kTo30k':
                return price > 10000 && price <= 30000;
            case 'over30k':
                return price > 30000;
            case 'custom':
                const min = customMinPrice ? parseInt(customMinPrice) : 0;
                const max = customMaxPrice ? parseInt(customMaxPrice) : Infinity;
                return price >= min && price <= max;
            default:
                return true;
        }
    };

    // 검색 필터링 함수
    const filterBySearch = (product: Product) => {
        if (!debouncedSearchTerm) return true;
        return product.groupTitle.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    };

    // 필터 적용
    useEffect(() => {
        const filtered = products.filter((product) => filterByPrice(product) && filterBySearch(product));
        setFilteredProducts(filtered);
    }, [products, priceRange, customMinPrice, customMaxPrice, debouncedSearchTerm]);

    // 필터 모달 열기/닫기 핸들러
    const handleFilterOpen = () => {
        setTempMinPrice(customMinPrice);
        setTempMaxPrice(customMaxPrice);
        setFilterOpen(true);
    };

    const handleFilterClose = () => {
        setFilterOpen(false);
    };

    // 검색창 확장/축소 핸들러
    const handleSearchExpandClick = () => {
        setIsSearchExpanded(true);
    };

    const handleSearchClose = () => {
        setIsSearchExpanded(false);
        setSearchTerm('');
    };

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: 2000,
                mx: 'auto',
                p: { xs: '16px', sm: 1, md: '40px', lg: '40px' },
                mt: { xs: 2, md: 6 },
            }}
        >
            {/* 헤더 영역 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: 16, md: 20 } }}>
                    {isBestNewPage
                        ? 'Best 신상'
                        : isTodayNewPage
                          ? '오늘의신상'
                          : isDiscountPage
                            ? '할인 상품'
                            : isSoldOutPage
                              ? '품절 상품'
                              : isNewProductPage
                                ? '오늘의신상'
                                : '전체상품'}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                    {isMobile ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: isSearchExpanded ? 120 : 32,
                                    transition: 'width 0.3s ease-in-out',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                {isSearchExpanded ? (
                                    <>
                                        <TextField
                                            size="small"
                                            placeholder="상품명 검색"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            autoFocus
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: '#666', fontSize: 18 }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={handleSearchClose}
                                                            edge="end"
                                                            sx={{ padding: 0 }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                    height: 32,
                                                    borderRadius: 3,
                                                    '& fieldset': {
                                                        borderColor: '#ddd',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#bbb',
                                                    },
                                                    '& input': {
                                                        fontSize: 12,
                                                        padding: '4px 8px',
                                                    },
                                                },
                                            }}
                                        />
                                    </>
                                ) : (
                                    <IconButton
                                        onClick={handleSearchExpandClick}
                                        sx={{
                                            padding: 0,
                                            color: '#666',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <SearchIcon sx={{ fontSize: 24 }} />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <TextField
                            size="small"
                            placeholder={isMobile ? '상품명을 검색하세요' : '상품명 검색'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#666', fontSize: { xs: 18, sm: 24 } }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: 120, sm: 200, md: 250 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    height: { xs: 32, sm: 40 },
                                    '& fieldset': {
                                        borderColor: '#ddd',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#bbb',
                                    },
                                    '& input': {
                                        fontSize: { xs: 12, sm: 14 },
                                        padding: { xs: '4px 8px', sm: '8.5px 14px' },
                                    },
                                },
                            }}
                        />
                    )}
                    <IconButton
                        onClick={handleFilterOpen}
                        sx={{
                            color: '#666',
                            padding: isMobile ? 0 : 'auto',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        <Badge
                            variant="dot"
                            invisible={priceRange === 'all'}
                            sx={{
                                '& .MuiBadge-badge': {
                                    backgroundColor: theme.palette.primary.main,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                    top: 2,
                                    right: 2,
                                },
                            }}
                        >
                            <FilterListIcon sx={{ fontSize: isMobile ? 24 : 'inherit' }} />
                        </Badge>
                    </IconButton>
                </Stack>
            </Box>

            {/* 필터 모달 */}
            {isMobile ? (
                <Drawer
                    anchor="bottom"
                    open={filterOpen}
                    onClose={handleFilterClose}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '80vh',
                        },
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                borderBottom: '1px solid #eee',
                                pb: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
                                필터
                            </Typography>
                            <IconButton edge="end" onClick={handleFilterClose} sx={{ color: '#666' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 2 }}>가격</Typography>
                            <Box>
                                <RadioGroup value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                        <FormControlLabel
                                            value="all"
                                            control={<Radio size="small" />}
                                            label="전체"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="under10k"
                                            control={<Radio size="small" />}
                                            label="10,000원 이하"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="10kTo30k"
                                            control={<Radio size="small" />}
                                            label="10,000원 ~ 30,000원"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="over30k"
                                            control={<Radio size="small" />}
                                            label="30,000원 이상"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="custom"
                                            control={<Radio size="small" />}
                                            label="직접입력"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                    </Box>
                                </RadioGroup>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{
                                        mt: 1,
                                        pl: 1,
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        placeholder="0"
                                        value={tempMinPrice}
                                        onChange={(e) => {
                                            setTempMinPrice(e.target.value);
                                            if (e.target.value) setPriceRange('custom');
                                        }}
                                        type="number"
                                        disabled={priceRange !== 'custom'}
                                        sx={{
                                            width: 152,
                                            '& .MuiOutlinedInput-root': {
                                                height: 36,
                                                borderRadius: 1,
                                                fontSize: 14,
                                                backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : 'transparent',
                                                '& fieldset': {
                                                    borderColor: '#ddd',
                                                },
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: priceRange !== 'custom' ? '#999' : '#666',
                                            fontSize: 14,
                                        }}
                                    >
                                        ~
                                    </Typography>
                                    <TextField
                                        size="small"
                                        placeholder="0"
                                        value={tempMaxPrice}
                                        onChange={(e) => {
                                            setTempMaxPrice(e.target.value);
                                            if (e.target.value) setPriceRange('custom');
                                        }}
                                        type="number"
                                        disabled={priceRange !== 'custom'}
                                        sx={{
                                            width: 152,
                                            '& .MuiOutlinedInput-root': {
                                                height: 36,
                                                borderRadius: 1,
                                                fontSize: 14,
                                                backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : 'transparent',
                                                '& fieldset': {
                                                    borderColor: '#ddd',
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        disabled={priceRange !== 'custom'}
                                        onClick={() => {
                                            if (tempMinPrice || tempMaxPrice) {
                                                setPriceRange('custom');
                                                setCustomMinPrice(tempMinPrice);
                                                setCustomMaxPrice(tempMaxPrice);
                                            }
                                        }}
                                        sx={{
                                            height: 36,
                                            minWidth: 60,
                                            ml: 1,
                                            fontSize: 14,
                                            borderColor: '#ddd',
                                            borderRadius: 1,
                                            color: '#666',
                                            '&:hover': {
                                                borderColor: '#999',
                                                backgroundColor: 'transparent',
                                            },
                                            '&.Mui-disabled': {
                                                color: '#999',
                                            },
                                        }}
                                    >
                                        적용
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleFilterClose}
                                sx={{
                                    width: '95%',
                                    borderRadius: 2,
                                    bgcolor: '#222',
                                    '&:hover': {
                                        bgcolor: '#000',
                                    },
                                }}
                            >
                                확인
                            </Button>
                        </Box>
                    </Box>
                </Drawer>
            ) : (
                <Dialog
                    open={filterOpen}
                    onClose={handleFilterClose}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            m: 2,
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #eee',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
                            필터
                        </Typography>
                        <IconButton edge="end" onClick={handleFilterClose} sx={{ color: '#666' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 2 }}>가격</Typography>
                            <Box>
                                <RadioGroup value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                        <FormControlLabel
                                            value="all"
                                            control={<Radio size="small" />}
                                            label="전체"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="under10k"
                                            control={<Radio size="small" />}
                                            label="10,000원 이하"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="10kTo30k"
                                            control={<Radio size="small" />}
                                            label="10,000원 ~ 30,000원"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="over30k"
                                            control={<Radio size="small" />}
                                            label="30,000원 이상"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <FormControlLabel
                                            value="custom"
                                            control={<Radio size="small" />}
                                            label="직접입력"
                                            sx={{
                                                m: 0,
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                    </Box>
                                </RadioGroup>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{
                                        mt: 1,
                                        pl: 1,
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        placeholder="0"
                                        value={tempMinPrice}
                                        onChange={(e) => {
                                            setTempMinPrice(e.target.value);
                                            if (e.target.value) setPriceRange('custom');
                                        }}
                                        type="number"
                                        disabled={priceRange !== 'custom'}
                                        sx={{
                                            width: 152,
                                            '& .MuiOutlinedInput-root': {
                                                height: 36,
                                                borderRadius: 1,
                                                fontSize: 14,
                                                backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : 'transparent',
                                                '& fieldset': {
                                                    borderColor: '#ddd',
                                                },
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: priceRange !== 'custom' ? '#999' : '#666',
                                            fontSize: 14,
                                        }}
                                    >
                                        ~
                                    </Typography>
                                    <TextField
                                        size="small"
                                        placeholder="0"
                                        value={tempMaxPrice}
                                        onChange={(e) => {
                                            setTempMaxPrice(e.target.value);
                                            if (e.target.value) setPriceRange('custom');
                                        }}
                                        type="number"
                                        disabled={priceRange !== 'custom'}
                                        sx={{
                                            width: 152,
                                            '& .MuiOutlinedInput-root': {
                                                height: 36,
                                                borderRadius: 1,
                                                fontSize: 14,
                                                backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : 'transparent',
                                                '& fieldset': {
                                                    borderColor: '#ddd',
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        disabled={priceRange !== 'custom'}
                                        onClick={() => {
                                            if (tempMinPrice || tempMaxPrice) {
                                                setPriceRange('custom');
                                                setCustomMinPrice(tempMinPrice);
                                                setCustomMaxPrice(tempMaxPrice);
                                            }
                                        }}
                                        sx={{
                                            height: 36,
                                            minWidth: 60,
                                            ml: 1,
                                            fontSize: 14,
                                            borderColor: '#ddd',
                                            borderRadius: 1,
                                            color: '#666',
                                            '&:hover': {
                                                borderColor: '#999',
                                                backgroundColor: 'transparent',
                                            },
                                            '&.Mui-disabled': {
                                                color: '#999',
                                            },
                                        }}
                                    >
                                        적용
                                    </Button>
                                </Stack>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleFilterClose}
                                sx={{
                                    width: '95%',
                                    borderRadius: 2,
                                    bgcolor: '#222',
                                    '&:hover': {
                                        bgcolor: '#000',
                                    },
                                }}
                            >
                                확인
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                    gap: { xs: 1, md: 3 },
                }}
            >
                {filteredProducts.map((apiProduct, index) => {
                    const product = transformApiToUiProduct(apiProduct);
                    // 카테고리 조회 대카테고리 > 소카테고리

                    return (
                        <Card
                            ref={index === filteredProducts.length - 1 ? lastProductElementRef : null}
                            key={product.id}
                            sx={{
                                borderRadius: 3,
                                boxShadow: 'none',
                                overflow: 'hidden',
                                position: 'relative',
                                transition: 'none',
                                cursor: 'pointer',
                                minWidth: 0,
                                border: 'none !important',
                                borderColor: 'transparent !important',
                                borderWidth: 0,
                                outline: 'none',
                                backgroundClip: 'padding-box',
                                backgroundColor: '#fff',
                            }}
                            onClick={() => handleOpen(apiProduct)}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover img': {
                                        transform: 'scale(1.07)',
                                    },
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={product.image}
                                    alt={product.title}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '3 / 4',
                                        objectFit: 'cover',
                                        border: '1px solid #eee',
                                        borderRadius: 2,
                                        transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
                                        minHeight: { xs: 120, md: 180 },
                                    }}
                                />
                                {/* {isLoggedIn && product.isSale && (
                                    <Chip
                                        label="SALE"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            left: 8,
                                            bottom: 8,
                                            bgcolor: '#222',
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            zIndex: 2,
                                            borderRadius: 1,
                                            px: 1.2,
                                            py: 0.5,
                                        }}
                                    />
                                )} */}
                            </Box>
                            <CardContent sx={{ p: { xs: 1, md: 1.5 }, display: 'grid', rowGap: 1 }}>
                                <Box sx={{ height: { xs: 16, md: 20 } }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: { xs: 11, md: 13 },
                                            lineHeight: '18px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            mb: 0,
                                        }}
                                    >
                                        {product.title}
                                    </Typography>
                                </Box>
                                <Box>
                                    {/* {isLoggedIn && product.isSale && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#bbb',
                                                mb: '1px',
                                                textDecoration: 'line-through',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: { xs: 11, md: 13 },
                                                    lineHeight: '18px',
                                                }}
                                            >
                                                ₩{product.originalPrice?.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    )} */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            justifyContent: 'space-between',
                                            gap: '4px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            {isLoggedIn ? (
                                                <>
                                                    {/* {product.isSale && (
                                                        <Typography
                                                            sx={{
                                                                color: '#FF4B6B',
                                                                fontWeight: 700,
                                                                fontSize: { xs: 12, md: 14 },
                                                                lineHeight: '22px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            {product.discount}%
                                                        </Typography>
                                                    )} */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            wordBreak: 'break-all',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 800,
                                                                fontSize: { xs: 13, md: 16 },
                                                                lineHeight: '22px',
                                                                color: '#222',
                                                                mb: 0,
                                                            }}
                                                        >
                                                            ₩{product.price.toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        wordBreak: 'break-all',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: { xs: 11, md: 12 },
                                                            lineHeight: '22px',
                                                            color: '#666',
                                                            mb: 0,
                                                        }}
                                                    >
                                                        판매가 회원공개
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
            {loading && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography>Loading...</Typography>
                </Box>
            )}
            <ProductModal open={open} onClose={handleClose} product={selectedProduct} />

            {/* SNS 플로팅 바 */}
            <SNSFloatingBar snsInfo={vendor?.vendorBusinessInfo?.snsInfo || []} />
        </Box>
    );
};

export default ProductList;
